"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import { useState, useTransition, useEffect } from "react"
import { useRouter } from "next/navigation"
import TopNavBar from "../../components/common/TopNavBar"
import SnackbarMessage from "../../components/common/SnackbarMessage"
import { useQuery } from "@apollo/client/react"
import publicProviderMessageable from "../../graphql/queries/publicProviderMessageable"
import CheckoutHeader from "../../components/pages/checkout/CheckoutHeader"
import StripeSetupHandler from "../../components/pages/payments/shared/StripeSetupHandler"
import LeftSideContainer from "../../components/pages/checkout/LeftSideContainer"
import RightSideContainer from "../../components/pages/checkout/RightSideContainer"
import SeatTicketCard from "../../components/pages/checkout/SeatTicketCard"
import ScheduleAndSessionDates from "../../components/pages/checkout/ScheduleAndSessionDates"
import AttendeeName from "../../components/pages/checkout/AttendeeName"
import AttendanceRequirements from "../../components/pages/checkout/AttendanceRequirements"
import CancellationPolicy from "../../components/pages/checkout/CancellationPolicy"
import CheckoutButtonSection from "../../components/pages/checkout/CheckoutButtonSection"
import TherapistMessageButton from "../../components/pages/checkout/TherapistMessageButton"
import Loader from "../../components/common/Loader"
import useGetMe from "../../hooks/useGetMe"
import { getSession } from "next-auth/react"
import {
  Cohort,
  Group,
  Pricing,
  Provider,
  SessionDate,
  SessionInfo,
} from "../../components/pages/checkout/types"

const FORM_ID_FOR_SANDBOX =
  process.env.NEXT_PUBLIC_HIPAATIZER_CONTACT_THERAPIST_FORM ?? ""

type Props = {
  cohortId: number | null
  userId: number | null
  isProrated: boolean
  requiresPayment: boolean
  latestPricing: Pricing
  cohort: Cohort
  group: Group
  isTestGroup: boolean
  isIndefinite: boolean
  consultRequired: boolean
  requiredToResideInStatesArray: string[]
  isParticipantRequiredToResideInSpecificState: boolean
  clientHasPaymentMethod: boolean | null
  clientPaymentMethodBrand: string | null
  clientPaymentMethodLast4: string | null
  sessionInfo: SessionInfo
  nextSessions: SessionDate[]
  nextSessionsTotalCount: number
  provider: Provider
}

type PublicProviderMessageableData = {
  publicProviderMessageable?: {
    nodes?: Array<{ canMessage?: boolean }>
  }
}

export default function CheckoutPage({
  cohortId,
  userId,
  isProrated: _isProrated,
  requiresPayment,
  latestPricing,
  cohort,
  group,
  isTestGroup: _isTestGroup,
  isIndefinite: _isIndefinite,
  consultRequired,
  requiredToResideInStatesArray,
  isParticipantRequiredToResideInSpecificState:
    _isParticipantRequiredToResideInSpecificState,
  clientHasPaymentMethod: _clientHasPaymentMethod,
  clientPaymentMethodBrand: _clientPaymentMethodBrand,
  clientPaymentMethodLast4: _clientPaymentMethodLast4,
  sessionInfo,
  nextSessions,
  nextSessionsTotalCount,
  provider,
}: Props) {
  const router = useRouter()
  const [{ me }] = useGetMe()
  const [cognitoId, setCognitoId] = useState<string | null>(null)
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false)
  const [consultRequiredAcknowledge, setConsultRequiredAcknowledge] = useState(false)
  const [stateResidenceRequiredAcknowledge, setStateResidenceRequiredAcknowledge] =
    useState(false)
  const [isPending, startTransition] = useTransition()

  const { data: publicProviderMessageableData } =
    useQuery<PublicProviderMessageableData>(publicProviderMessageable, {
      variables: { iProviderId: provider?.id },
      skip: !provider?.id,
    })
  const isProviderMessageable =
    publicProviderMessageableData?.publicProviderMessageable?.nodes?.[0]?.canMessage

  useEffect(() => {
    const fetchCognitoId = async () => {
      const session = await getSession()
      const user = session?.user as Record<string, unknown> | undefined
      setCognitoId((user?.sub as string | null) ?? null)
    }
    void fetchCognitoId()
  }, [])

  const groupName = group?.name ?? null
  const groupId = group?.id ?? null
  const isLoggedIn = !!userId

  return (
    <div className="">
      <TopNavBar startingBackgroundSolid={true} />
      {isPending && <Loader />}
      <div className={"bg-black min-h-[calc(100vh-64px)] flex  justify-center"}>
        <div
          className={
            "max-w-[1312px] w-full flex flex-col pb-[50px] px-[10px] min-[600px]:px-[20px]"
          }
        >
          <CheckoutHeader />
          <div className={"w-full flex flex-col min-[1100px]:flex-row items-center"}>
            <LeftSideContainer>
              <div
                className={
                  "flex flex-col py-[10px] min-[600px]:py-[20px] min-[800px]:py-[29px]"
                }
              >
                <SeatTicketCard
                  group={group}
                  cohort={cohort}
                  latestPricing={latestPricing}
                  sessionInfo={sessionInfo}
                />
                <ScheduleAndSessionDates
                  cohort={cohort}
                  cohortId={cohortId}
                  sessionInfo={sessionInfo}
                  nextSessions={nextSessions}
                  nextSessionsTotalCount={nextSessionsTotalCount}
                />
              </div>
            </LeftSideContainer>
            <RightSideContainer>
              <AttendeeName me={me} isLoggedIn={isLoggedIn} />
              <AttendanceRequirements
                consultRequiredAcknowledge={consultRequiredAcknowledge}
                setConsultRequiredAcknowledge={setConsultRequiredAcknowledge}
                stateResidenceRequiredAcknowledge={stateResidenceRequiredAcknowledge}
                setStateResidenceRequiredAcknowledge={
                  setStateResidenceRequiredAcknowledge
                }
                consultRequired={consultRequired}
                requiredToResideInStatesArray={requiredToResideInStatesArray}
              />
              <CancellationPolicy latestPricing={latestPricing} cohort={cohort} />
              <TherapistMessageButton
                formId={FORM_ID_FOR_SANDBOX}
                cognitoId={cognitoId}
                providerId={provider?.id}
                userId={userId}
                groupName={groupName}
                groupId={groupId}
                groupLeaderUserId={provider?.userId}
                publicProviderMessageable={isProviderMessageable}
              />
              <CheckoutButtonSection
                isLoggedIn={isLoggedIn}
                cohortId={cohortId}
                cohort={cohort}
                latestPricing={latestPricing}
                requiresPayment={requiresPayment}
              />
            </RightSideContainer>
          </div>
        </div>
      </div>
      <SnackbarMessage />

      {addPaymentDialogOpen ? (
        <StripeSetupHandler
          onClose={async () => {
            startTransition(() => {
              router.refresh()
              setAddPaymentDialogOpen(false)
            })
          }}
        />
      ) : null}
    </div>
  )
}
